import ks from "../ReUsableTS/kBrowserShared";
import { kEdTblSelectionMode, kEditableTableActive, kHTMLEditableColType } from "./kEditableTableTypes";
import { kEditableTableCell } from "./kEditableTableCell";
import type { kEditableTable } from "./kEditableTable";
import type { kEditableTableRow } from "./kEditableTableRow";
import type { kEditableColumn } from "./kEditableColumn";
import type { kEditableTableHeading } from "./kEditableTableHeading";

// Member bodies of kEditableTable moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableTable member keeps its signature and delegates here.
export class kEditableTableKeys {
    // True when KeyDown must leave the event alone: a bare modifier key, a
    // hidden or inactive table, an open rich HTML popup (every key belongs to
    // the editor; the popup handles Escape via its own document listener), or
    // a printable character aimed at a contenteditable cell.
    public static _KeyDownShouldIgnore(Self: kEditableTable, KeyEvent:KeyboardEvent): boolean {
        if (ks.KErrorMode === true) { return true; }
        try {
            if(KeyEvent.ctrlKey === true && KeyEvent.key === "Control" ||KeyEvent.shiftKey=== true && KeyEvent.key === "Shift"){
                return true; //Ignore just the shift and ctrl key events
            }
            if(Self.Table !== null && (Self.Table as HTMLElement).offsetParent === null){
                return true;
            }
            if (kEditableTableActive.ActiveTable !== Self) {
                return true;
            }
            if (Self.CurrenEditableCell && Self.CurrenEditableCell.HtmlPopup !== null) {
                return true;
            }
            if (Self.CurrenEditableCell && Self.CurrenEditableCell.Cell.contentEditable === "true") {
                if (Self["_IsTextEditCaretKey"](KeyEvent) === true) {
                    return true;
                }
            }
            return Self["_KeyTargetBelongsElsewhere"](KeyEvent.target);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._KeyDownShouldIgnore");
            return true;
        }
    }

    // True when the key's TARGET element says the key is not this grid's:
    //  - Focus is inside a modal dialog IN FRONT of this grid (e.g. the
    //    kEditableTableGeoCell map-picker, or any kModalDialog opened from a cell):
    //    the grid must NOT hijack Tab / arrows for cell navigation (that cancels the
    //    dialog's own focus traversal). Ignore when the key's target sits in a
    //    [role=dialog] that does NOT contain this grid — a grid hosted INSIDE a dialog
    //    still handles its own keys (that dialog contains this.Table).
    //  - LeaveForeignFieldKeys is on and the target is a form field outside this grid.
    public static _KeyTargetBelongsElsewhere(Self: kEditableTable, Target: EventTarget | null): boolean {
        if (ks.KErrorMode === true) { return true; }
        try {
            if (Self.Table === null || Target === null || !(Target instanceof HTMLElement)) {
                return false;
            }
            const DialogHost: Element | null = Target.closest("[role=dialog]");
            if (DialogHost !== null && DialogHost.contains(Self.Table) === false) {
                return true;
            }
            if (Self.LeaveForeignFieldKeys === true && Self["_IsForeignField"](Target) === true) {
                return true;
            }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._KeyTargetBelongsElsewhere");
            return true;
        }
    }

    // A text field, text area, select or contenteditable element that is NOT part of this grid:
    // a key typed there belongs to that field (see LeaveForeignFieldKeys).
    public static _IsForeignField(Self: kEditableTable, Target: HTMLElement): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Self.Table !== null && Self.Table.contains(Target) === true) { return false; }
            const Tag: string = Target.tagName;
            return Tag === "INPUT" || Tag === "TEXTAREA" || Tag === "SELECT" || Target.isContentEditable === true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._IsForeignField");
            return false;
        }
    }

    // Excel-style type-to-edit. Returns true (and enters edit mode on the pressed
    // character) when a bare printable key lands on a single selected text cell
    // that is not already editing. Returns false for everything else (named keys,
    // Ctrl/Alt/Meta chords, structured-editor columns, locked/read-only cells) so
    // the normal navigation / shortcut handling downstream is untouched.
    public static _TryTypeToEdit(Self: kEditableTable, KeyEvent:KeyboardEvent): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            // Bare printable character only: no Ctrl/Alt/Meta (Shift is allowed for
            // capitals). key.length === 1 excludes Enter/Tab/Arrow*/F2/Delete/etc.
            // Space is left to its existing role (row-select / checkbox toggle).
            if (KeyEvent.ctrlKey === true || KeyEvent.altKey === true || KeyEvent.metaKey === true) { return false; }
            if (KeyEvent.key.length !== 1) { return false; }
            if (KeyEvent.key === " ") { return false; }
            // Target the active cell; fall back to the selection's anchor cell so a
            // range selection (which clears CurrenEditableCell) still types into its
            // top-left cell rather than swallowing the key.
            let Target:kEditableTableCell|null = Self.CurrenEditableCell;
            if (Target === null) { Target = Self.Selection.GetTopLeftCell(); }
            if (Target === null) { return false; }
            if (Target.Cell.contentEditable === "true") { return false; }
            if (Target.ColDef === undefined) { return false; }
            if (Target.ColDef.Locked !== false) { return false; }
            // People / link / geo / rich-html cells own their editor via
            // CustomEditor — typing must not clobber their structured value.
            if (Target.ColDef.CustomEditor !== null) { return false; }
            if (Target.ColDef.Type !== kHTMLEditableColType.Text
                && Target.ColDef.Type !== kHTMLEditableColType.Number
                && Target.ColDef.Type !== kHTMLEditableColType.Currency) { return false; }
            const OwningRow:kEditableTableRow|undefined = Self.Rows.Items[Target.RowIndex];
            if (OwningRow !== undefined && OwningRow.ReadOnly === true) { return false; }
            // Collapse any multi-cell range to this single cell, then enter edit
            // mode seeding the pressed character (type-over).
            Self.Selection.SetSingleCell(Target);
            Target.BeginTextEditWithChar(KeyEvent.key);
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._TryTypeToEdit");
            return false;
        }
    }

    public static KeyDown(Self: kEditableTable, KeyEvent:KeyboardEvent): void {
        if (ks.KErrorMode === true) { return; }
        try {
            let Handled:boolean = false;
            if (Self["_KeyDownShouldIgnore"](KeyEvent) === true) {
                return;
            }
            // Excel-style type-to-edit: a printable key on a selected-but-not-yet-
            // editing text cell enters edit mode typing that character. Runs before
            // the modifier split so shifted characters (capitals / symbols) count.
            // _KeyDownShouldIgnore already let printable keys through when the cell
            // IS in contentEditable mode, so reaching here means it is not.
            // ...and Alt+ArrowDown opens the active cell's column filter (its funnel is not a tab stop).
            if (Self["_TryTypeToEdit"](KeyEvent) === true || Self["_TryOpenFilterKey"](KeyEvent) === true) {
                KeyEvent.preventDefault();
                KeyEvent.stopPropagation();
                return;
            }
            if (KeyEvent.ctrlKey === false) {
                if (KeyEvent.shiftKey === false) {
                    Handled = Self["_HandleNoModifierKey"](KeyEvent.key);
                } else { //Shift events
                    Handled = Self.ShiftKeyEvents(KeyEvent.key);
                }
            } else { //CTRL key keydown event
                Handled = Self.CTRLKeyevents(KeyEvent.key, KeyEvent);
            }
            if(Handled === true){
                KeyEvent.preventDefault();
                KeyEvent.stopPropagation();
                return;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys.KeyDown");
            return;
        }
    }

    public static ShiftKeyEvents(Self: kEditableTable, key:string): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            let Handled:boolean = false;
            switch (key) {
                case "Tab":
                    Handled = Self["_ShiftTab"]();
                    break;
                case "ArrowDown":
                    Self.Selection.ExpandDown();
                    break;
                case "ArrowUp":
                    Self.Selection.ExpandUp();
                    Handled = true;
                    break;
                case "ArrowRight":
                    Self.Selection.ExpandRight();
                    Handled = true;
                    break;
                case "ArrowLeft":
                    Self.Selection.ExpandLeft();
                    Handled = true;
                    break;
                case " ":
                    Handled = Self["_ShiftSpaceSelectsRow"]();
            }
            return Handled;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys.ShiftKeyEvents");
            return false;
        }
    }

    /** Moved from kEditableTable._ShiftTab (size split); the class member delegates here. */
    public static _ShiftTab(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Self["_TabLeavesGrid"]() === true) { return false; }
            if(Self.CurrenEditableCell){
                if (Self.CurrenEditableCell.ColumnIndex > 0) {
                    Self.CurrenEditableCell.SelectNextLeft();
                } else {
                    Self.CurrenEditableCell.SelectPrevRowLastCell();
                }
                return true;
            }
            const TopLeft: kEditableTableCell | null = Self.Selection.GetTopLeftCell();
            if (TopLeft) { TopLeft.SelectNextLeft(); return true; }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._ShiftTab");
            return false;
        }
    }

    /** Moved from kEditableTable._ShiftSpaceSelectsRow (size split); the class member delegates here. */
    public static _ShiftSpaceSelectsRow(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if(Self.Selection.SelectionMode !== kEdTblSelectionMode.SingleCell){ return false; }
            const SelectedCells:kEditableTableCell[] = Self.Selection.GetSelectedCells();
            if(SelectedCells.length !== 1){ return false; }
            const CurrentRow:kEditableTableRow = Self.Rows.Items[SelectedCells[0].RowIndex];
            if(CurrentRow){
                Self.Selection.SetSingleRowSelected(CurrentRow);
                return true;
            }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._ShiftSpaceSelectsRow");
            return false;
        }
    }

    public static CTRLKeyevents(Self: kEditableTable, key:string, KeyEvent:KeyboardEvent): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if(KeyEvent.shiftKey === true){ return Self["_CtrlShiftKey"](key); }
            return Self["_CtrlOnlyKey"](key);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys.CTRLKeyevents");
            return false;
        }
    }

    /** Moved from kEditableTable._CtrlShiftKey (size split); the class member delegates here. */
    public static _CtrlShiftKey(Self: kEditableTable, key:string): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            switch (key) {
                case " ":
                    Self.Selection.SelectAll();
                    return true;
                case "End":
                    return Self["_CtrlShiftEnd"]();
                case "+":
                    Self.InsertRowAbove();
                    return true;
            }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._CtrlShiftKey");
            return false;
        }
    }

    /** Moved from kEditableTable._CtrlShiftEnd (size split); the class member delegates here. */
    public static _CtrlShiftEnd(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            switch(Self.Selection.SelectionMode){
                case kEdTblSelectionMode.SingleCell:
                case kEdTblSelectionMode.CustomExtRightDown:
                    Self.Selection.ColIndexEnd = Self.Columns.Items.length-1;
                    Self.Selection.SetSelectionByIndexes(kEdTblSelectionMode.CustomExtRightDown);
                    return true;
                case kEdTblSelectionMode.CustomExtRightUp:
                    Self.Selection.ColIndexEnd = Self.Columns.Items.length-1;
                    Self.Selection.SetSelectionByIndexes(kEdTblSelectionMode.CustomExtRightUp);
                    return true;
                case kEdTblSelectionMode.CustomExtLeftDown:
                    Self.Selection.RowIndexEnd = Self.Rows.Items.length -1;
                    Self.Selection.ColIndexEnd = Self.Columns.Items.length -1;
                    Self.Selection.SetSelectionByIndexes(kEdTblSelectionMode.CustomExtRightDown);
                    return true;
            }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._CtrlShiftEnd");
            return false;
        }
    }

    /** Moved from kEditableTable._CtrlOnlyKey (size split); the class member delegates here. */
    public static _CtrlOnlyKey(Self: kEditableTable, key:string): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            switch (key) {
                case " ": return Self["_CtrlSpaceSelectsColumn"]();
                case "a": Self.Selection.SelectAll(); return true;
                case "c": Self.CopyClick(); return true;
                case "z": Self.Undo(); return true;
                case "y": Self.Redo(); return true;
                case "Home": Self.GetCellByIndexes(0, 0).Select(); return true;
                case "End":
                    Self.GetCellByIndexes(Self.Rows.Items.length - 1, Self.Columns.Items.length - 1).Select();
                    return true;
                case "-": Self.RemoveRowContextMenuClick(); return true;
                case "d":
                    if(Self.Selection.RowIndexEnd > Self.Selection.RowIndexStart){ Self.FillDown(); }
                    return true;
                case "r":
                    if(Self.Selection.ColIndexEnd > Self.Selection.ColindexStart){ Self.FillRight(); }
                    return true;
            }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._CtrlOnlyKey");
            return false;
        }
    }

    /** Moved from kEditableTable._CtrlSpaceSelectsColumn (size split); the class member delegates here. */
    public static _CtrlSpaceSelectsColumn(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if(Self.Selection.SelectionMode !== kEdTblSelectionMode.SingleCell){ return false; }
            const SelectedCells:kEditableTableCell[] = Self.Selection.GetSelectedCells();
            if(SelectedCells.length !== 1){ return false; }
            const CurrentCol:kEditableColumn = Self.Columns.Items[SelectedCells[0].ColumnIndex];
            if(CurrentCol){
                Self.Selection.SetSingleColSelected(CurrentCol);
                return true;
            }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._CtrlSpaceSelectsColumn");
            return false;
        }
    }

    // Arrow / Enter cursor movement. Split out of _HandleNoModifierKey (which was
    // 123 lines) — one switch per behaviour family, dispatched in the original
    // order. Each helper returns false for a key it does not own, so the three
    // are tried in turn and the observable result is identical to the single
    // switch it replaced.
    public static _HandleArrowKey(Self: kEditableTable, Key: string): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            let Handled: boolean = false;
            // Same fallback the four original cases each spelled out: act on the
            // cell being edited, else on the selection's top-left cell. ArrowRight
            // alone falls back to the top-RIGHT cell (so it steps off the right
            // edge of a range) — preserved exactly.
            let Target: kEditableTableCell | null = Self.CurrenEditableCell;
            if (Key === "ArrowDown" || Key === "Enter") {
                if (!Target) { Target = Self.Selection.GetTopLeftCell(); }
                if (Target && Target === Self.CurrenEditableCell && Self.BlankRowsAllowed() === false
                    && Target.RowIndex + 1 >= Self.Rows.Items.length) {
                    // Last row, no blank row to move to: commit the edit instead of
                    // asking for a row that does not exist (the editor stayed open).
                    Target.SetCellReadMode();
                    Self.CurrenEditableCell = null;
                    Handled = true;
                } else if (Target) { Target.SelectNextDown(); Handled = true; }
            } else if (Key === "ArrowUp") {
                if (!Target) { Target = Self.Selection.GetTopLeftCell(); }
                if (Target) { Target.SelectNextUp(); Handled = true; }
            } else if (Key === "ArrowLeft") {
                if (!Target) { Target = Self.Selection.GetTopLeftCell(); }
                if (Target) { Target.SelectNextLeft(); Handled = true; }
            } else if (Key === "ArrowRight") {
                if (Target) {
                    Target.SelectNextRight(); Handled = true;
                } else {
                    const TopRight: kEditableTableCell | null = Self.Selection.GetTopRigthCell();
                    if (TopRight) { TopRight.Select(); Handled = true; }
                }
            }
            return Handled;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._HandleArrowKey");
            return false;
        }
    }

    // Space / F2 (enter-edit gesture) and Tab (advance one cell, wrapping rows).
    public static _HandleEditGestureKey(Self: kEditableTable, Key: string): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Key === " " || Key === "F2") { return Self["_HandleEnterEditGesture"](); }
            if (Key === "Tab") { return Self["_HandleTabKey"](); }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._HandleEditGestureKey");
            return false;
        }
    }

    // Space / F2 on the current cell - the explicit "open the editor" gesture.
    // With no current cell there is nothing to open, so the grid falls back to
    // selecting the range's top-left, which is what gives an empty grid a way in.
    public static _HandleEnterEditGesture(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Self.CurrenEditableCell === null || !Self.CurrenEditableCell.ColDef) {
                const TopLeft: kEditableTableCell | null = Self.Selection.GetTopLeftCell();
                if (TopLeft) { TopLeft.Select(); return true; }
                return false;
            }
            let Handled: boolean = false;
            const ColType: kHTMLEditableColType = Self.CurrenEditableCell.ColDef.Type;
            if (ColType === kHTMLEditableColType.Checkbox || ColType === kHTMLEditableColType.Radio) {
                Self.CurrenEditableCell.CellClick(); Handled = true;
            }
            if (ColType === kHTMLEditableColType.RichHtml) {
                // Keyboard navigation only selects RichHtml cells;
                // F2/space is the explicit "open the editor" gesture.
                Self.CurrenEditableCell.CellClick(); Handled = true;
            }
            // F2 on a Text/Number/Currency cell (already contentEditable
            // from selection): switch ready -> ACTIVE edit with the caret
            // at the end (Excel's F2), so the caret keys now act on the
            // cell text. Space never reaches here for such a cell — it is
            // deferred to the browser as a printable character.
            if (Self.CurrenEditableCell.Cell.contentEditable === "true") {
                Self._InlineEditActive = true;
                Self.CurrenEditableCell.PlaceCaretAtEnd();
                Handled = true;
            }
            return Handled;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._HandleEnterEditGesture");
            return false;
        }
    }

    // Tab: advance one cell, wrapping to the next row's first cell past the end.
    public static _HandleTabKey(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Self["_TabLeavesGrid"]() === true) { return false; }
            if (Self.CurrenEditableCell) {
                if ((Self.CurrenEditableCell.ColumnIndex + 1) < Self.Columns.Items.length) {
                    Self.CurrenEditableCell.SelectNextRight();
                } else {
                    Self.CurrenEditableCell.SelectNextRowFirstCell();
                }
                return true;
            }
            const TopRight: kEditableTableCell | null = Self.Selection.GetTopRigthCell();
            if (TopRight) { TopRight.Select(); return true; }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._HandleTabKey");
            return false;
        }
    }

    // Delete / Escape / End / Home.
    public static _HandleCellCommandKey(Self: kEditableTable, Key: string): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Key === "Delete") { return Self["_HandleDeleteKey"](); }
            if (Key === "Escape") { return Self["_HandleEscapeKey"](); }
            if (Key === "End" || Key === "Home") { return Self["_HandleHomeEndKey"](Key); }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._HandleCellCommandKey");
            return false;
        }
    }

    // Delete clears the current cell's value — unless the table, the row or the COLUMN says no.
    public static _HandleDeleteKey(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Self.CurrenEditableCell === null) { return false; }
            const DelRow: kEditableTableRow = Self.Rows.Items[Self.CurrenEditableCell.RowIndex];
            // Delete BLANKS a cell, so it is a write and every protection applies to it — the table's
            // own read-only mode, the row's, and the COLUMN's. The column half was missing until
            // 2026-08-25, which made `Locked` mean "cannot be typed into" rather than "cannot be
            // written": on a partly-locked grid an operator could select a locked cell and empty it
            // with one keystroke. Paste and fill-down both already refused on `Locked`, so this is the
            // third write path agreeing with the other two rather than a new rule.
            if (Self.IsEffectivelyReadOnly() === true) { return true; }
            if (DelRow && DelRow.ReadOnly === true) { return true; }
            if (Self.CurrenEditableCell.ColDef && Self.CurrenEditableCell.ColDef.Locked === true) {
                return true;
            }
            Self.CurrenEditableCell.SetNewValue("", true);
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._HandleDeleteKey");
            return false;
        }
    }

    // End / Home jump within the row, but only from a single-cell selection -
    // from a range they would silently destroy the range the user just built.
    public static _HandleHomeEndKey(Self: kEditableTable, Key: string): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Self.Selection.SelectionMode !== kEdTblSelectionMode.SingleCell) { return false; }
            if (Key === "End") { Self.Selection.SelectLastCell(); return true; }
            Self.Selection.SelectFirstCell();
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._HandleHomeEndKey");
            return false;
        }
    }

    public static _HandleNoModifierKey(Self: kEditableTable, Key: string): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Self["_EnterFromKeyboard"](Key) === true) { return true; }
            if (Key === "Enter" && Self["_ActivateCellControl"]() === true) { return true; }
            if (Self["_HandleArrowKey"](Key) === true) { return true; }
            if (Self["_HandleEditGestureKey"](Key) === true) { return true; }
            return Self["_HandleCellCommandKey"](Key);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._HandleNoModifierKey");
            return false;
        }
    }

    // The keyboard just tabbed into the grid (OnTableFocus): nothing is selected yet, so the first
    // arrow / Home selects the FIRST cell on screen - not the one below row 0, and not a row the
    // filter or the pager hides. A selection that already exists (mouse, range) is left alone.
    public static _EnterFromKeyboard(Self: kEditableTable, Key: string): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Home"].indexOf(Key) === -1) { return false; }
            if (Self.CurrenEditableCell !== null) { return false; }
            if (Self.Selection.GetSelectedCells().length > 0) { return false; }
            let Row: kEditableTableRow;
            for (Row of Self.Rows.Items) {
                if (Row.IsOnScreen() === true && Row.Cells.length > 0) {
                    Row.Cells[0].Select();
                    return true;
                }
            }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._EnterFromKeyboard");
            return false;
        }
    }

    // Enter on a cell (not in inline edit) activates its SOLE link or button - exactly the control
    // KeepOutOfTabOrder took out of the tab order (same rule: kEditableTableCell.SoleActivatableControl).
    // Any other cell keeps Enter's old meaning (move down); its controls are still tab stops.
    public static _ActivateCellControl(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            const Current: kEditableTableCell | null = Self.CurrenEditableCell;
            if (Current === null || Current.Cell === null) { return false; }
            // Not contentEditable: a selected text cell is contentEditable in "ready" mode already
            // (measured); _InlineEditActive is the grid's own "typing in it" state.
            if (Self._InlineEditActive === true) { return false; }
            const Control: HTMLElement | null = kEditableTableCell.SoleActivatableControl(Current.Cell);
            if (Control === null) { return false; }
            Control.click();
            return true;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._ActivateCellControl");
            return false;
        }
    }

    // Alt+ArrowDown (only that chord) -> _OpenActiveColumnFilter. False for any other key.
    public static _TryOpenFilterKey(Self: kEditableTable, KeyEvent: KeyboardEvent): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (KeyEvent.altKey !== true || KeyEvent.key !== "ArrowDown") { return false; }
            return Self["_OpenActiveColumnFilter"]();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._TryOpenFilterKey");
            return false;
        }
    }

    // Alt+ArrowDown: open the filter of the active cell's column through its funnel (which is no longer
    // a tab stop). False when there is no active cell or the column has no funnel.
    public static _OpenActiveColumnFilter(Self: kEditableTable): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Self.CurrenEditableCell === null) { return false; }
            const ColumnIndex: number = Self.CurrenEditableCell.ColumnIndex;
            let Heading: kEditableTableHeading;
            for (Heading of Self.Headers) {
                if (Heading.ColumnIndex === ColumnIndex && Heading.FilterIcon !== null) {
                    Heading.FilterIcon.click();
                    return true;
                }
            }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys._OpenActiveColumnFilter");
            return false;
        }
    }

    public static CheckSelectionChange(Self: kEditableTable): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.Table !== null && (Self.Table as HTMLElement).offsetParent === null){
                return;
            }
            if(Self.CurrenEditableCell){
                if(Self.CurrenEditableCell.UniqueID !== Self["_prevSelectionUnique"]){
                    Self["_prevSelectionUnique"] = Self.CurrenEditableCell.UniqueID;
                    if(Self.CellSelectionChangedCB){
                        Self.CellSelectionChangedCB(Self.CurrenEditableCell);
                    }
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys.CheckSelectionChange");
            return;
        }
    }

    public static SetParentSize(Self: kEditableTable, Height:number): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.Table) {
                const ParentElement: HTMLElement | null = Self.Table.parentElement;
                if (ParentElement) {
                    ParentElement.style.width = Self.Table.clientWidth + 40 + "px";
                    ParentElement.style.height = Height + "px";
                    ParentElement.style.overflowY = "scroll";
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableKeys.SetParentSize");
            return;
        }
    }
}
